import type { SvgIconComponent } from "@mui/icons-material";
import ChevronRightOutlinedIcon from "@mui/icons-material/ChevronRightOutlined";
import { Box, Divider, Typography } from "@mui/material";
import { shape } from "@app/design-tokens";
import type { ReactNode } from "react";
import { Link } from "react-router";

import { useAuth } from "@/auth/authContext";
import { PageHeader } from "@/layout/PageHeader";
import { serviceIcon } from "@/layout/serviceIcons";

import {
  ADMIN_TOPICS,
  GENERAL_TOPICS,
  canSeeAdminGuide,
  guideHref,
  serviceGuideDescription,
} from "./topics";

/**
 * 使用指南的第一層：一份分組的目錄。
 *
 * **刻意不用「服務」落地頁那種大卡片。** 兩頁長得一樣的時候，使用者分不出
 * 自己是在「進服務」還是在「看服務的教學」—— 曾經真的做成同一組卡片，
 * 一眼看不出差別。這裡改成文件目錄的樣子：分組標題、緊湊的列、線框圖示、
 * 每列一句「這篇教什麼」，而不是服務自己的說明。
 *
 * 網址放在 `/account/guide`：第一層導覽固定四節，使用指南的入口在右上角的
 * 帳號選單，跟個人中心同一組，所以 rail 亮在「個人中心」、麵包屑的祖先是
 * 個人中心。
 */

function TopicRow({
  to,
  icon: Icon,
  label,
  description,
}: {
  to: string;
  icon: SvgIconComponent;
  label: string;
  description: string;
}) {
  return (
    <Box
      component={Link}
      to={to}
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 1.5,
        py: 1.25,
        px: 1,
        mx: -1,
        borderRadius: `${shape.medium}px`,
        color: "text.primary",
        "&:hover": { bgcolor: "action.hover" },
      }}
    >
      <Icon fontSize="small" sx={{ color: "text.secondary", flex: "none" }} />
      <Box sx={{ minWidth: 0, flexGrow: 1 }}>
        <Typography variant="subtitle2" component="h3">
          {label}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {description}
        </Typography>
      </Box>
      <ChevronRightOutlinedIcon fontSize="small" sx={{ color: "text.secondary", flex: "none" }} />
    </Box>
  );
}

function Group({
  title,
  caption,
  children,
}: {
  title: string;
  caption: string;
  children: ReactNode;
}) {
  return (
    <Box
      component="section"
      sx={{ border: 1, borderColor: "divider", borderRadius: `${shape.large}px`, p: 2.5 }}
    >
      <Typography variant="subtitle1" component="h2" sx={{ fontWeight: 700 }}>
        {title}
      </Typography>
      <Typography variant="caption" color="text.secondary">
        {caption}
      </Typography>
      <Divider sx={{ mt: 1.5 }} />
      <Box sx={{ mt: 0.5, display: "flex", flexDirection: "column" }}>{children}</Box>
    </Box>
  );
}

export function GuideIndexPage() {
  const { me } = useAuth();
  const services = me?.services ?? [];
  const showAdmin = canSeeAdminGuide(me);

  return (
    <>
      <PageHeader
        crumbs={[{ label: "個人中心", to: "/account" }, { label: "使用指南" }]}
        title="使用指南"
        description="整個系統的操作手冊：從登入開始，到各服務與後台管理。"
      />

      <Box sx={{ flexGrow: 1, minHeight: 0, overflow: "auto", px: 3, pb: 3 }}>
        <Box
          sx={{
            display: "grid",
            gap: 2,
            gridTemplateColumns: { xs: "minmax(0, 1fr)", lg: "repeat(2, minmax(0, 1fr))" },
            alignItems: "start",
          }}
        >
          <Group title="開始使用" caption="不分服務，每個人都會用到的基本操作。">
            {GENERAL_TOPICS.map((topic) => (
              <TopicRow
                key={topic.key}
                to={guideHref("general", topic.key)}
                icon={topic.icon}
                label={topic.label}
                description={topic.description}
              />
            ))}
          </Group>

          <Group title="服務" caption="您可以使用的服務，各自的操作說明。">
            {services.length === 0 ? (
              <Typography variant="body2" color="text.secondary" sx={{ py: 1.25 }}>
                您尚未被授予任何服務權限，所以這裡沒有服務的指南。
              </Typography>
            ) : (
              services.map((service) => (
                <TopicRow
                  key={service.code}
                  to={guideHref("services", service.code)}
                  icon={serviceIcon(service.code)}
                  label={service.name}
                  description={serviceGuideDescription(service.name)}
                />
              ))
            )}
          </Group>

          {showAdmin && (
            <Group title="後台管理" caption="給管理員：在 Django Admin 維護帳號、角色與服務。">
              {ADMIN_TOPICS.map((topic) => (
                <TopicRow
                  key={topic.key}
                  to={guideHref("admin", topic.key)}
                  icon={topic.icon}
                  label={topic.label}
                  description={topic.description}
                />
              ))}
            </Group>
          )}
        </Box>
      </Box>
    </>
  );
}
